import { Lock } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, Caption } from '@liveauctioneers/hammer-ui-core/text';

import { ReviewedPageComment } from '@/pages/Review/ReviewedPage/components/ReviewedPageComment/ReviewedPageComment';
import { ReviewComment, ReviewCommentPrivacyStatus, ReviewResponse } from '@/types/Review';
import { Seller } from '@/types/Seller';

type Props = {
    completedReview: ReviewResponse;
    reviewComments: ReviewComment[];
    seller: Seller;
};

export const ReviewedPageComments = ({ completedReview, reviewComments, seller }: Props) => {
    const { note, review, reviewHeadline } = completedReview;

    const publicComments =
        Boolean(reviewComments) &&
        reviewComments
            .filter(({ privacyStatus }) => privacyStatus === ReviewCommentPrivacyStatus.Public)
            .map((reviewComment) => (
                <ReviewedPageComment
                    comment={reviewComment}
                    key={`reviewed-page.comments.public-comment.${reviewComment.commentId}`}
                    logoId={seller.logoId}
                />
            ));

    const privateComments =
        Boolean(reviewComments) &&
        reviewComments
            .filter(({ privacyStatus }) => privacyStatus === ReviewCommentPrivacyStatus.Private)
            .map((reviewComment) => (
                <ReviewedPageComment
                    comment={reviewComment}
                    key={`reviewed-page.comments.private-comment.${reviewComment.commentId}`}
                    logoId={seller.logoId}
                />
            ));

    const showPublic = Boolean(review) || Boolean(reviewHeadline) || publicComments.length > 0;
    const showPrivate = Boolean(note) || privateComments.length > 0;

    return (
        <div
            className="mt-sm flex justify-start pb-lg"
            data-testid="comments-container"
        >
            <div className="flex w-[calc(100%-273px)] flex-col justify-start xsMax:w-full">
                {showPublic && (
                    <div
                        aria-label="public-review"
                        className="flex flex-col"
                    >
                        <h2>
                            <FormattedMessage id="review_page.labels.public_review" />
                        </h2>
                        {reviewHeadline && (
                            <BodyPrimary className="mt-xs max-w-[700px] font-bold">{reviewHeadline}</BodyPrimary>
                        )}
                        {review && <BodyPrimary className="mt-2xs mb-2xs max-w-[700px]">{review}</BodyPrimary>}
                        {publicComments}
                    </div>
                )}
                {showPrivate && (
                    <div>
                        <h2>
                            <FormattedMessage id="review_page.labels.private_feedback" />{' '}
                        </h2>
                        <div
                            aria-label="private-review"
                            className="mt-sm flex w-[83%] bg-background-subdued px-md pt-xs pb-sm xsMax:w-full"
                        >
                            <div className="flex w-full flex-col">
                                {note && <BodyPrimary className="mt-2xs mb-2xs max-w-[700px]">{note}</BodyPrimary>}
                                {privateComments}
                                <div className="flex items-center justify-end pt-sm uppercase">
                                    <Caption color="secondary">
                                        <FormattedMessage id="review_page.labels.private_feedback_label" />
                                        <Lock
                                            className="ml-2xs inline-block"
                                            size="xs"
                                            style="fill"
                                        />
                                    </Caption>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
